import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import cx from 'classnames';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import { MenuItem as MenuItemType } from '../../types';
import { useCurrentUser } from '../../utils/currentUser';
import matchesCondition from '../../utils/conditions';
import { useFeatures } from '../../utils/features';
import { useCurrentLocale } from '../../utils/translations';
import MenuHeader from './MenuHeader';
import MenuItem from './MenuItem';
import styles from './Menu.css';
import getLocalizedUrl from '../../utils/urls';

type Props = {
  classNames?: {
    root?: string;
    header?: string;
    items?: string;
    item?: string;
  };
  dataTestId?: string;
  icon?: string | ReactNode;
  items: MenuItemType[];
  label: string;
  name: string;
  onClick?: (
    name: string,
    details?: { [key: string]: string | undefined },
    event?: Event
  ) => void;
};

const Menu: FC<Props> = ({
  classNames,
  dataTestId,
  icon,
  items,
  label,
  name,
  onClick
}) => {
  const { isLoggedIn } = useCurrentUser();
  const locale = useCurrentLocale();
  const featureFlags = useFeatures();
  const { buttonProps, itemProps, isOpen, setIsOpen } = useDropdownMenu(
    items.length
  );

  const handleMenuItemClick = useCallback(
    (
      menuItemName: string,
      details?: { [key: string]: string | undefined },
      event?: Event
    ) => {
      if (isOpen) {
        setIsOpen(false);
      }
      onClick?.(menuItemName, details, event);
    },
    [isOpen, setIsOpen]
  );

  const enabledItems = useMemo(
    () =>
      items.filter(
        (item) =>
          !item?.condition ||
          matchesCondition(item?.condition, {
            featureFlags,
            isLoggedIn
          })
      ),
    [items, isLoggedIn, featureFlags]
  );

  return (
    <div className={classNames?.root} role="menuitem">
      <MenuHeader
        icon={icon}
        label={label}
        name={name}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...buttonProps}
        dataTestId={dataTestId}
      />
      <div
        className={cx(
          styles.itemsWrapper,
          {
            [styles.hidden]: !isOpen || !items.length
          },
          classNames?.items
        )}
        role="menu"
      >
        {enabledItems.map((item, itemIndex) => (
          <div
            key={item.name}
            className={cx({
              [styles.minWidthSmall]: item.condition?.mediaQuery === 'small',
              [styles.minWidthMedium]: item.condition?.mediaQuery === 'medium',
              [styles.minWidthLarge]: item.condition?.mediaQuery === 'large',
              [styles.maxWidthSmall]: item.condition?.mediaQuery === '!small',
              [styles.maxWidthMedium]: item.condition?.mediaQuery === '!medium',
              [styles.maxWidthLarge]: item.condition?.mediaQuery === '!large'
            })}
            role="menuitem"
          >
            <MenuItem
              badge={item.badge}
              className={classNames?.item}
              icon={item.icon}
              isMasked={item.crawlable === false}
              label={item.label}
              name={item.name}
              type={item.type}
              dataTestId={item.testId}
              url={getLocalizedUrl(item.url, locale)}
              onClick={handleMenuItemClick}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...itemProps[itemIndex]}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Menu;
